import React from 'react'
import { useParams } from 'react-router-dom'
import { CompletionStatus, SectionName } from '_gql'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { epochToDate } from '@sputnik/components/utils'
import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import AddPersonDrawer, {
  type AddPersonDrawerOpenable,
} from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { AddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

const AddAuthorizedSignerDrawer = ({ isShowing, onClose }: AddPersonDrawerOpenable) => {
  const { appId } = useParams()
  const showToast = useToast()
  const { track } = useWebTracking()

  const completionStatus = useApplicationProgress({ sectionName: SectionName.PEOPLE_INFO })

  const [updateAuthorizedSigner] = useUpdatePeopleInfoData({
    onCompleted: (updatedData) => {
      const peopleInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
        (section) =>
          section?.sectionName === SectionName.BUSINESS_INFO &&
          section?.completionStatus === CompletionStatus.COMPLETED,
      )

      if (peopleInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
        track('se_signup__application_people_info_section_progress__completed', {
          is_complete: true,
        })
      }

      logInfo('updateAuthorizedSigner', {
        attributes: {
          creditAppId: appId,
          result: `authorized signer added successfully`,
        },
        eventName: 'updateAuthorizedSigner',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Authorized signer added'
          id='sputnik.AddAuthorizedSignerDrawer__Jyul+E'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      onClose()
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Unable to add authorized signer'
          id='sputnik.AddAuthorizedSignerDrawer__eZr8J6'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'updateAuthorizedSigner',
          creditAppId: appId,
          result: error,
          message: 'Error while adding authorized signer.',
        },
        eventName: 'updateAuthorizedSigner',
      })
    },
  })

  const onSubmit = async (formValues: AddPersonForm) => {
    track('se_signup__add_person_drawer_saved__fired', {
      person_type: 'authorizedSigner',
    })

    await updateAuthorizedSigner({
      variables: {
        creditApplicationId: appId ?? '',
        authorizedSigner: {
          dob: epochToDate(formValues.dob) || '',
          email: formValues.email || '',
          firstName: formValues.firstName || '',
          lastName: formValues.lastName || '',
          title: formValues.title || '',
          phoneNumber: formValues.phoneNumber || '',
          ssn: formValues.ssn?.value || '',
          passportCountry: formValues.passportCountry || '',
          passportNumber: formValues.passportNumber?.value || '',
          passportExpiration: formValues.passportExpiration || '',
          address: {
            street: formValues.street || '',
            streetAdditional: formValues.streetAdditional || '',
            city: formValues.city || '',
            state: formValues.state || '',
            postalCode: formValues.postalCode || '',
            countryCode: formValues.countryCode || 'US',
          },
        },
      },
    })
  }

  return (
    isShowing && (
      <AddPersonDrawer
        isAuthorizedSigner
        isShowing={isShowing}
        title={
          <FormattedMessage
            defaultMessage='Add an authorized signer'
            id='sputnik.AddAuthorizedSignerDrawer__ZaNlPj'
          />
        }
        onClose={onClose}
        onSubmit={onSubmit}
      />
    )
  )
}

export default AddAuthorizedSignerDrawer
